import { Table, type TableProps } from '@omnipresentgroup/design-system';
import type {
  Employment,
  GetEmploymentsResponse,
} from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';

import { ColumnHeaders, useEmploymentsTableConfig } from './hooks';

const EMPTY_ARRAY: Employment[] = [];
const LOADING_ARRAY = Array.from({ length: 3 }, () => ({}) as Employment);

type Props = {
  data?: GetEmploymentsResponse;
  isLoading?: boolean;
  isError?: boolean;
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders)[];
  columnsOverride?: ColumnDef<Employment, unknown>[];
  emptyStateProps?: TableProps['emptyStateProps'];
  optionsOverride?: Omit<TableOptions<Employment>, 'data' | 'columns'>;
  onRowClick?: (row: Employment) => void;
  useRowSelection?: boolean;
  tableActions?: TableProps['tableActions'];
  showHeader?: boolean;
  onEditClick?: (row: Employment) => void;
};

const sortEmploymentsByStartDate = (a: Employment, b: Employment): number => {
  const dateA = a.startDate
    ? DateTime.fromISO(a.startDate)
    : DateTime.fromISO(a.createdAt);
  const dateB = b.startDate
    ? DateTime.fromISO(b.startDate)
    : DateTime.fromISO(b.createdAt);

  return dateB.toMillis() - dateA.toMillis();
};

export const EmploymentsList = ({
  data,
  isLoading,
  isError,
  columnVisibility,
  columnOrderOverride,
  columnsOverride,
  emptyStateProps,
  optionsOverride,
  onRowClick,
  useRowSelection,
  tableActions,
  showHeader = true,
  onEditClick,
}: Props) => {
  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const sortedEmployments = useMemo(() => {
    return data ? [...data].sort(sortEmploymentsByStartDate) : [];
  }, [data]);

  const tableOptions: Omit<TableOptions<Employment>, 'data' | 'columns'> = {
    state: {
      columnVisibility: columnVisibilityState,
      columnOrder,
    },
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibilityState,
    onColumnOrderChange: setColumnOrder,
    ...optionsOverride,
  };

  const { columns } = useEmploymentsTableConfig({
    columnsOverride,
    onEditClick,
  });

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : sortedEmployments || EMPTY_ARRAY,
    columns,
    getRowId: (row: Employment) => row.id,
    ...tableOptions,
  });

  return (
    <Table
      id="employments-list"
      table={table}
      loading={isLoading}
      showErrorState={isError}
      useSorting={false}
      usePagination={false}
      onRowClick={
        onRowClick
          ? (row: Employment) => {
              !isLoading && !isError && onRowClick(row);
            }
          : undefined
      }
      emptyStateProps={emptyStateProps}
      useRowSelection={useRowSelection}
      tableActions={tableActions}
      showHeader={showHeader}
    />
  );
};
